import { Box, Button } from '@mui/material';
import React, { useEffect } from 'react';
import { useContext, useState } from 'react';
import { AppContext } from '../../../AppContext';
import { Actions } from '../../../enums/ActionEnums';
import { Types } from '../../../enums/TypeEnums';
import {
  IInvoice,
  IPayInvoiceMutation,
  useCreateCreditNoteMutation,
  useDeleteInvoiceMutation,
  useInvoicesForMembershipQuery,
  usePayInvoiceMutation
} from '../../../generated/graphql';
import { canUserCreate } from '../../../utils/rightUtils';
import PaperCard from '../../Common/Layout/PaperCard';
import { parseDate, removeInvoiceFromCache } from '../../../utils/clientUtils';
import { INVOICE_STATUS_CONFIRMED } from '../../../enums/AppConsts';
import moment from 'moment';
import { Colors } from '../../../utils/theme';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import credit from '../../../assets/images/actions/credit.svg';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import credit_gray from '../../../assets/images/actions/credit_gray.svg';
import { getInvoiceProductInfoDto } from '../../Invoices/InvoiceList';
import DenseTable from '../../Common/Tables/DenseTable';
import { FormatTableData } from '../../../utils/styleUtils';
import { DATE_TIME_FORMATS } from '../../../utils/constants';
import { IHeadCell } from '../../Common/Tables/OverviewTable';
import EpraDatePicker from '../../Common/Fields/EpraDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import InvoiceStepper from '../../Invoices/Wizard/InvoiceStepper';

type IProps = {
  membershipId: number;
};

const MembershipInvoicesTab: React.FunctionComponent<IProps> = ({ membershipId }: IProps) => {
  const [openWizard, setOpenWizard] = useState(false);
  const { data, loading } = useInvoicesForMembershipQuery({
    variables: { membership_id: membershipId },
    fetchPolicy: 'no-cache'
  });
  const [invoices, setInvoices] = useState<IInvoice[]>([]);
  const [deleteInvoice, deleteInvoiceResult] = useDeleteInvoiceMutation();
  const [updatePaidDate, updatePaidDateResult] = usePayInvoiceMutation();
  const { dispatch, state } = useContext(AppContext);
  const [createCreditNote, createCreditResult] = useCreateCreditNoteMutation();
  const canCreate = canUserCreate(state.user, Types.Invoice);

  useEffect(() => {
    if (data && data.result) {
      setInvoices(data.result as IInvoice[]);
    }
  }, [data]);

  const handlePayDateChange = (datePaid: Date | null, rowData: IInvoice) => {
    const createdDate = parseDate(rowData.created_date) as Date;
    if (rowData.status.name === INVOICE_STATUS_CONFIRMED) {
      if (datePaid !== null) {
        updatePaidDate({
          variables: {
            id: parseInt(rowData.id as string),
            date_paid: datePaid.toString()
          },
          update(_, { data }) {
            updatePaidDateFinished(data);
          }
        });
      } else {
        updatePaidDate({
          variables: {
            id: parseInt(rowData.id as string)
          },
          update(_, { data }) {
            updatePaidDateFinished(data);
          }
        });
      }
    }
  };

  const updatePaidDateFinished = (data: IPayInvoiceMutation | null | undefined) => {
    if (data && data.result) {
      const { result } = data;
      const updatedInvoice = invoices.map((i) => (i.id === result.id ? result : i));
      setInvoices(updatedInvoice as never[]);
      dispatch({
        type: Actions.ShowMessage,
        payload: {
          severity: 'success',
          text: `Invoice ${result.number} updated`,
          autoHide: 6000
        }
      });
    }
  };

  const formatDate = (rowData: IInvoice, name: string) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return rowData[name]
      ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        moment(parseInt(rowData[name] as string)).format(DATE_TIME_FORMATS.DATE)
      : '';
  };

  const renderStatus = (status: string) => {
    return (
      <p style={{ color: status === INVOICE_STATUS_CONFIRMED ? Colors.green : Colors.red }}>
        {status}
      </p>
    );
  };

  const renderCreditButton = (id: string, isCredit: boolean, statusName: string) => {
    if (statusName === INVOICE_STATUS_CONFIRMED && !isCredit) {
      return (
        <Button
          id={`credit-button-${id}`}
          sx={{ height: '38px', width: 'auto' }}
          onClick={() => handleCreditPressed(id)}
          disabled={!canCreate}
        >
          <img className="credit" src={canCreate ? credit : credit_gray} alt="creditStatus" />
        </Button>
      );
    }
  };

  const handleCreditPressed = (id: string) => {
    dispatch({
      type: Actions.ShowConfirmation,
      payload: {
        text: 'Are you sure you want to create credit note?',
        agreeAction: handleCreateCredit,
        params: id
      }
    });
  };

  const handleCreateCredit = (id: string) => {
    createCreditNote({
      variables: {
        id: Number(id)
      },
      update(_, { data }) {
        if (data?.result) {
          dispatch({
            type: Actions.SetInvoice,
            payload: {
              invoice: data.result as IInvoice,
              productInfo: getInvoiceProductInfoDto(data.result as IInvoice)
            }
          });
          handleNewInvoice(data.result as IInvoice);
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'success',
              text: `You have successfully created new credit note`
            }
          });
        }
      }
    })
      .catch((e) => {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: e.message,
            autoHide: 6000
          }
        });
      })
      .finally(() => {
        dispatch({
          type: Actions.HideConfirmation
        });
      });
  };

  const handleDetails = (rowData: IInvoice) => {
    setOpenWizard(true);
    dispatch({
      type: Actions.SetInvoice,
      payload: {
        invoice: rowData,
        productInfo: getInvoiceProductInfoDto(rowData)
      }
    });
  };

  const renderCalendar = (rowData: IInvoice) => {
    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <EpraDatePicker
          id="membership-tab-invoices-date-paid-picker"
          disabled={false}
          label={''}
          value={rowData?.date_paid ? parseDate(rowData.date_paid) : null}
          onChange={(newPaidDate) => handlePayDateChange(newPaidDate, rowData as IInvoice)}
        />
      </LocalizationProvider>
    );
  };

  const boldNumber = (invoiceNumber: string) => {
    return <b>{invoiceNumber}</b>;
  };
  const remove = (rowData: IInvoice) =>
    dispatch({
      type: Actions.ShowConfirmation,
      payload: {
        text: `Are you sure you want to delete current pending invoice?`,
        agreeAction: handleDelete,
        params: rowData.id
      }
    });

  const handleDelete = (id: string | undefined) => {
    if (!id) {
      return;
    }
    deleteInvoice({
      variables: {
        id: parseInt(id)
      },
      update() {
        dispatch({
          type: Actions.ShowMessage,
          payload: { text: `Invoice deleted ${id}`, severity: 'success' }
        });

        const deletedInvoice = invoices.find((i) => i.id === id);
        removeInvoiceFromCache(deletedInvoice as IInvoice);
        setInvoices(invoices.filter((i) => i.id !== id));
      }
    })
      .catch((e) => {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: e.message,
            autoHide: 6000
          }
        });
      })
      .finally(() => {
        dispatch({
          type: Actions.HideConfirmation
        });
      });
  };

  const handleNewInvoice = (invoice: IInvoice) => {
    setInvoices([invoice, ...(invoices || [])]);
  };

  const handleCreateNewPressed = () => {
    setOpenWizard(true);
    dispatch({
      type: Actions.SetInvoice,
      payload: {
        invoice: null
      }
    });
  };

  const handleEditInvoice = (invoice: IInvoice) => {
    setInvoices(invoices?.map((i) => (i?.id === invoice.id ? invoice : i)));
  };

  const columns = [
    {
      title: 'Details',
      sorting: false,
      width: 51
    },
    {
      title: 'Invoice',
      field: 'number',
      sorting: false,
      width: 95,
      render: (rowData: IInvoice) => boldNumber(rowData.number || '')
    },
    {
      title: 'Date',
      field: 'created_date',
      type: 'date',
      width: 77,
      render: (rowData: IInvoice) => formatDate(rowData, 'created_date')
    },
    {
      title: 'Due',
      field: 'due_date',
      type: 'date',
      render: (rowData: IInvoice) => formatDate(rowData, 'due_date'),
      width: 85
    },
    {
      title: 'Total',
      field: 'total',
      width: 56
    },
    {
      title: 'Membership',
      field: 'membership.id',
      sorting: false,
      width: 48
    },
    {
      title: 'Product',
      field: 'product.product_code.name',
      width: 21
    },
    {
      title: 'Date Paid',
      width: 455,
      render: (rowData: IInvoice) => renderCalendar(rowData)
    },
    {
      title: 'Status',
      field: 'status.name',
      width: 80,
      render: (rowData: IInvoice) => renderStatus(rowData.status.name)
    },
    {
      title: 'Company',
      field: 'details.company_name',
      width: 133
    },
    {
      title: 'Contact',
      field: 'details.fullName',
      width: 135
    },
    {
      title: 'Street',
      field: 'details.street',
      width: 136
    },
    {
      title: 'City',
      field: 'details.city',
      width: 78
    },
    {
      title: 'Credit',
      field: 'is_credit',
      width: 58,
      render: (rowData: IInvoice) =>
        renderCreditButton(rowData.id, rowData.is_credit, rowData.status.name)
    },
    {
      title: 'Remove',
      sorting: false,
      field: 'status.name',
      width: 71
    }
  ] as IHeadCell[];

  return (
    <Box className="centralize" sx={{ width: '100%', paddingTop: '80px' }}>
      <PaperCard
        headerText="Invoices"
        width="82%"
        additionalStyle={{ height: '680px' }}
        createButtonProps={{
          id: 'create-invoice-button-membership-details-invoices-tab',
          onClick: () => handleCreateNewPressed(),
          text: '+ CREATE INVOICE',
          disabled: !canCreate
        }}
      >
        <DenseTable
          columns={columns}
          data={FormatTableData(invoices)}
          loading={loading}
          handleDetails={handleDetails}
          handleRemove={remove}
          pageSize={10}
          crudType={Types.Membership}
          additionalToolbar={true}
          fixedHeaders={true}
        />
      </PaperCard>
      {openWizard && (
        <InvoiceStepper
          onClose={() => setOpenWizard(false)}
          addNewInvoice={handleNewInvoice}
          editInvoice={handleEditInvoice}
        />
      )}
    </Box>
  );
};
export default MembershipInvoicesTab;
